/**
 * hooks/useLogin.ts
 * Author: Sean Johnson <sean@scj.net>
 * Purpose: SRP login flow via amazon-cognito-identity-js — password never leaves browser
 * Last Updated: 2026-06-11
 * Version: 1.0.0
 *
 * Auth flow:
 *   1. authenticateUser() → Cognito SRP exchange (browser ↔ Cognito directly)
 *   2. On success: POST tokens to /api/auth/exchange-tokens → Lambda sets httpOnly cookies
 *   3. Call checkAuth() → GET /api/auth/me → populate Zustand store
 */

import { useState, useRef, useCallback } from 'react'
import { CognitoUser, CognitoUserPool, AuthenticationDetails, type CognitoUserSession } from 'amazon-cognito-identity-js'

// ─── Memory storage — tokens live only in httpOnly cookies, not localStorage ──

class MemoryStorage {
  private store = new Map<string, string>()
  setItem(k: string, v: string) { this.store.set(k, v) }
  getItem(k: string) { return this.store.get(k) ?? null }
  removeItem(k: string) { this.store.delete(k) }
  clear() { this.store.clear() }
}

const USER_POOL_ID = import.meta.env.VITE_COGNITO_USER_POOL_ID as string
const CLIENT_ID    = import.meta.env.VITE_COGNITO_CLIENT_ID as string
const memStorage   = new MemoryStorage()

function createPool() {
  return new CognitoUserPool({ UserPoolId: USER_POOL_ID, ClientId: CLIENT_ID, Storage: memStorage })
}

async function exchangeTokens(session: CognitoUserSession): Promise<boolean> {
  const res = await fetch('/api/auth/exchange-tokens', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      accessToken:  session.getAccessToken().getJwtToken(),
      refreshToken: session.getRefreshToken().getToken(),
      idToken:      session.getIdToken().getJwtToken(),
    }),
  })
  return res.ok
}

export type LoginChallenge = 'none' | 'new_password' | 'mfa'

export function useLogin() {
  const [email, setEmail]               = useState('')
  const [password, setPassword]         = useState('')
  const [newPassword, setNewPassword]   = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [mfaCode, setMfaCode]           = useState('')
  const [error, setError]               = useState('')
  const [loading, setLoading]           = useState(false)
  const [challenge, setChallenge]       = useState<LoginChallenge>('none')
  const cognitoUserRef                  = useRef<CognitoUser | null>(null)

  const fail = useCallback((msg: string) => {
    setError(msg)
    setLoading(false)
  }, [])

  const succeed = useCallback(async (session: CognitoUserSession, onDone: () => void) => {
    const ok = await exchangeTokens(session)
    if (ok) {
      onDone()
    } else {
      fail('Authentication failed — please try again')
    }
  }, [fail])

  // ─── Submit credentials (initial login) ────────────────────────────────────

  const submitLogin = useCallback((onDone: () => void) => {
    setError('')
    setLoading(true)

    const pool = createPool()
    const user = new CognitoUser({ Username: email, Pool: pool, Storage: memStorage })
    cognitoUserRef.current = user

    user.authenticateUser(
      new AuthenticationDetails({ Username: email, Password: password }),
      {
        onSuccess:           (s) => succeed(s, onDone),
        onFailure:           ()  => fail('Invalid email or password'),
        newPasswordRequired: ()  => { setLoading(false); setChallenge('new_password') },
        mfaRequired:         ()  => { setLoading(false); setChallenge('mfa') },
        totpRequired:        ()  => { setLoading(false); setChallenge('mfa') },
      }
    )
  }, [email, password, succeed, fail])

  // ─── Submit new password (NEW_PASSWORD_REQUIRED challenge) ─────────────────

  const submitNewPassword = useCallback((onDone: () => void) => {
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return }
    if (newPassword.length < 8)          { setError('Minimum 8 characters'); return }

    setError('')
    setLoading(true)

    cognitoUserRef.current?.completeNewPasswordChallenge(newPassword, {}, {
      onSuccess:    (s) => succeed(s, onDone),
      onFailure:    ()  => fail('Password change failed'),
      mfaRequired:  ()  => { setLoading(false); setChallenge('mfa') },
      totpRequired: ()  => { setLoading(false); setChallenge('mfa') },
    })
  }, [newPassword, confirmPassword, succeed, fail])

  // ─── Submit MFA code ────────────────────────────────────────────────────────

  const submitMfa = useCallback((onDone: () => void) => {
    setError('')
    setLoading(true)

    cognitoUserRef.current?.sendMFACode(
      mfaCode.trim(),
      {
        onSuccess: (s) => succeed(s, onDone),
        onFailure: ()  => { fail('Invalid code'); setMfaCode('') },
      },
      'SOFTWARE_TOKEN_MFA'
    )
  }, [mfaCode, succeed, fail])

  const resetChallenge = useCallback(() => {
    setChallenge('none')
    cognitoUserRef.current = null
    setMfaCode('')
    setError('')
  }, [])

  return {
    email, setEmail,
    password, setPassword,
    newPassword, setNewPassword,
    confirmPassword, setConfirmPassword,
    mfaCode, setMfaCode,
    error, loading, challenge,
    submitLogin, submitNewPassword, submitMfa,
    resetChallenge,
  }
}
